import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, ArrowRight, LinkedinLogo, EnvelopeSimple, Sparkle, Briefcase,
} from '@phosphor-icons/react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png';

// Founder + open executive roles. Bios/photos populate as each role is filled.
const TEAM = [
  {
    role: 'Founder & Acting CEO',
    title: 'Founder',
    name: 'Scott Lease',
    initials: 'SL',
    status: 'active',
    bio: 'Building Divispend after seeing how badly creators, side-hustlers, and small operators get served by today\'s payment rails. Sets product direction, fundraising, and partnerships.',
    background: 'Operator and consultant with hands-on experience across creator economy, marketplaces, and revenue-share business models. Wears every hat until the right co-founders arrive.',
    linkedin: '#',
    email: 'mailto:scott@divispend.com',
  },
  {
    role: 'Open Role — Hiring',
    title: 'COO',
    name: 'Chief Operating Officer',
    initials: 'COO',
    status: 'hiring',
    bio: 'Will own day-to-day operations, customer support, vendor relationships, and team performance. Reports directly to the founder.',
    background: 'Looking for an operator with 7+ years scaling support and ops at a high-growth fintech or marketplace. Comfortable being employee #2.',
    linkedin: null,
    email: 'mailto:careers@divispend.com?subject=COO%20role',
  },
  {
    role: 'Open Role — Hiring',
    title: 'CTO',
    name: 'Chief Technology Officer',
    initials: 'CTO',
    status: 'hiring',
    bio: 'Will own engineering org, technical architecture, security posture, and platform reliability. Inherits a working FastAPI/React/MongoDB stack with SOC 2 controls in place.',
    background: 'Looking for a senior engineering leader who has shipped large-scale payment systems and can recruit a tight founding engineering team.',
    linkedin: null,
    email: 'mailto:careers@divispend.com?subject=CTO%20role',
  },
  {
    role: 'Open Role — Hiring',
    title: 'CFO',
    name: 'Chief Financial Officer',
    initials: 'CFO',
    status: 'hiring',
    bio: 'Will own the books, fundraising strategy, financial planning, and money-transmission compliance for a US fintech.',
    background: 'Looking for an operator-CFO with prior fintech experience, CPA preferred, and deep familiarity with state-level money-transmitter licensing.',
    linkedin: null,
    email: 'mailto:careers@divispend.com?subject=CFO%20role',
  },
  {
    role: 'Open Role — Hiring',
    title: 'CPO',
    name: 'Chief Product Officer',
    initials: 'CPO',
    status: 'hiring',
    bio: 'Will own product roadmap, user research, and design. Translates user pain into the next shipped feature.',
    background: 'Looking for a product leader who has shipped flagship consumer fintech or marketplace products and ships in weekly cycles.',
    linkedin: null,
    email: 'mailto:careers@divispend.com?subject=CPO%20role',
  },
];

// PLACEHOLDER CONTENT — replace with real team info once collected from founder.
const _TEAM_LEGACY_UNUSED = [];

function TeamCard({ member }) {
  const hiring = member.status === 'hiring';
  return (
    <div
      className={`group rounded-2xl p-6 sm:p-8 transition-all duration-300 ease-out hover:-translate-y-1 ${
        hiring
          ? 'bg-[#0a0a0a] border border-dashed border-[#22C55E]/30 hover:border-[#22C55E]/60'
          : 'bg-[#0d150e] border border-[#22C55E]/10 hover:border-[#22C55E]/40 hover:shadow-[0_0_0_1px_rgba(34,197,94,0.15),0_24px_48px_rgba(0,0,0,0.5)]'
      }`}
      data-testid={`team-card-${member.title.toLowerCase()}`}
    >
      {hiring && (
        <div className="inline-flex items-center gap-1.5 mb-4 bg-[#22C55E]/15 border border-[#22C55E]/40 rounded-full px-3 py-1">
          <Briefcase className="w-3 h-3 text-[#22C55E]" weight="duotone" />
          <span className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#22C55E]">Hiring now</span>
        </div>
      )}
      <div className="flex items-start gap-5 mb-5">
        <div
          className={`w-20 h-20 sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center shrink-0 ${
            hiring
              ? 'bg-[#22C55E]/5 border border-dashed border-[#22C55E]/40'
              : 'bg-gradient-to-br from-[#22C55E]/25 to-[#22C55E]/5 border border-[#22C55E]/30'
          }`}
        >
          <span
            className="text-[#22C55E] text-xl sm:text-2xl font-black tracking-wider"
            style={{ fontFamily: 'Cabinet Grotesk' }}
          >
            {member.initials}
          </span>
        </div>
        <div className="min-w-0 flex-1 pt-1">
          <p className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#22C55E] mb-1">{member.role}</p>
          <h3
            className="text-xl sm:text-2xl font-bold text-white mb-0.5 leading-tight"
            style={{ fontFamily: 'Cabinet Grotesk' }}
          >
            {member.name}
          </h3>
          <p className="text-xs text-[#666666] uppercase tracking-wide">{member.title}</p>
        </div>
      </div>

      <p className="text-sm text-[#cccccc] leading-relaxed mb-3">{member.bio}</p>

      <p className="text-xs text-[#999999] leading-relaxed border-t border-[#22C55E]/10 pt-3 mb-4">
        <span className="text-[#22C55E] font-semibold uppercase tracking-wider text-[10px] block mb-1">
          {hiring ? 'Ideal candidate' : 'Background'}
        </span>
        {member.background}
      </p>

      <div className="flex gap-2">
        {member.linkedin && (
          <a
            href={member.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 rounded-md bg-[#111111] border border-[#22C55E]/20 flex items-center justify-center text-[#22C55E] hover:bg-[#22C55E]/10 transition-colors"
            aria-label={`${member.name} on LinkedIn`}
            data-testid={`team-linkedin-${member.title.toLowerCase()}`}
          >
            <LinkedinLogo className="w-4 h-4" weight="fill" />
          </a>
        )}
        {member.email && (
          <a
            href={member.email}
            className="w-8 h-8 rounded-md bg-[#111111] border border-[#22C55E]/20 flex items-center justify-center text-[#22C55E] hover:bg-[#22C55E]/10 transition-colors"
            aria-label={`Email ${member.name}`}
            data-testid={`team-email-${member.title.toLowerCase()}`}
          >
            <EnvelopeSimple className="w-4 h-4" weight="fill" />
          </a>
        )}
        {hiring && (
          <a
            href={member.email}
            className="ml-1 text-[11px] font-bold text-[#22C55E] hover:underline self-center"
            data-testid={`team-apply-${member.title.toLowerCase()}`}
          >
            Apply →
          </a>
        )}
      </div>
    </div>
  );
}

export default function OurTeamPage() {
  return (
    <div className="min-h-screen bg-[#111111]">
      {/* Header */}
      <header className="bg-[#111111]/80 backdrop-blur-xl border-b border-[#222222] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="team-logo-link">
            <img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" />
          </Link>
          <Link to="/about" className="text-sm text-[#999999] hover:text-white inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> About Us
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 sm:py-24 border-b border-[#222222]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Sparkle className="w-10 h-10 text-[#22C55E] mx-auto mb-5" weight="fill" />
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#22C55E] mb-4">Leadership Team</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6 leading-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
            One founder, four open seats.
          </h1>
          <p className="text-base sm:text-lg text-[#999999] max-w-2xl mx-auto leading-relaxed">
            Divispend is being built by a founder and a small bench of contractors. We&apos;re intentionally open about
            the seats we&apos;re hiring for — if any of them sound like you, the door is wide open.
          </p>
        </div>
      </section>

      {/* Team grid */}
      <section className="py-16 sm:py-20 border-b border-[#222222]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {TEAM.map((m) => (
              <TeamCard key={m.title} member={m} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Want to join the team?
          </h2>
          <p className="text-[#999999] mb-8 max-w-md mx-auto">
            We're always looking for builders who want to reshape how people earn. Drop us a line.
          </p>
          <a href="mailto:careers@divispend.com">
            <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-12 px-8 text-base font-semibold" data-testid="team-careers-button">
              careers@divispend.com <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#222222] py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={LOGO_URL} alt="Divispend" className="h-8 w-auto" />
          <p className="text-sm text-[#999999]">&copy; 2026 Divispend. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
